import {
  Header,
  Wrapper,
  ReplaceTitle,
  ProductList,
  ProductItem,
  LoaderWrapper,
  PlaceholderWrapper,
  SearchProductsWrapper,
  SearchInputWrapper,
  SortIconWrapper,
  CategorySelectorWrapper,
  CategoryName,
  CategoryRow,
  SearchRow,
  SearchIconWrapper,
} from '@/ui/screens/ReplenishmentPlan/ReplaceProductDrawer/ReplaceProductDrawer.styles.ts'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@/core/assets/icons/close.svg'
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material'
import { ComponentType, FC, useEffect, useState, MouseEvent } from 'react'
import { ProductCard } from '@/ui/components/ProductCard'
import { SearchInput } from '@/ui/components/SearchInput'
import { useDebouncedCallback } from 'use-debounce'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  FixedSizeList as _FixedSizeList,
  FixedSizeListProps,
  ListChildComponentProps,
} from 'react-window'
import { useSearchProductsToReplace } from '@/ui/screens/ReplenishmentPlan/ReplaceProductDrawer/useSearchProductsToReplace.ts'
import {
  Category,
  Product,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { ReplaceProductDialog } from './ReplaceProductDialog'
import { NothingFoundPlaceholder } from '@/ui/components/NothingFoundPlaceholder'
import ArrowDownIcon from '@/core/assets/icons/arrow-down.svg'
import ArrowUpIcon from '@/core/assets/icons/arrow-up.svg'
import ChevronDownIcon from '@/core/assets/icons/chevron-down.svg'
import SearchIcon from '@/core/assets/icons/search.svg'
import { useSelector } from 'react-redux'
import { selectCategories } from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'

const FixedSizeList = _FixedSizeList as ComponentType<FixedSizeListProps>

interface Props {
  product: Product | null
  onClose: () => void
}

const ReplaceProductDrawer: FC<Props> = ({ onClose, product }) => {
  const [replaceFromProduct, setReplaceFromProduct] = useState(product)

  useEffect(() => {
    if (product) {
      setReplaceFromProduct(product)
    }
  }, [product])

  const { t } = useTranslation()

  const categories = useSelector(selectCategories)

  const allCategory = { id: -1, code: '', name: t('replaceProductDrawer.all') }
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    product?.category ?? null,
  )
  const [categoryMenuAnchor, setCategoryMenuAnchor] =
    useState<HTMLElement | null>(null)

  useEffect(() => {
    if (product?.category) {
      setSelectedCategory(product.category)
    }
  }, [product])

  const [reverseProducts, setReverseProducts] = useState(false)

  const onSort = () => {
    setReverseProducts(!reverseProducts)
  }

  const [replaceToProduct, setReplaceToProduct] = useState<Product | null>(null)

  const onProductClick = (product: Product) => {
    setReplaceToProduct(product)
  }

  const [value, setValue] = useState('')

  const onChange = (value: string) => {
    setValue(value)
    setSelectedCategory(allCategory)

    if (!value) {
      debouncedClearSearch()
    } else {
      debouncedSearch(value, selectedCategory?.code)
    }
  }

  const onCloseReplaceDialog = () => {
    setReplaceToProduct(null)
  }

  const onCompleteReplace = () => {
    setReplaceToProduct(null)

    onClose()
  }

  const { searchProducts, isLoading, products, setProducts } =
    useSearchProductsToReplace()

  const sortedProducts = reverseProducts ? [...products].reverse() : products

  const debouncedClearSearch = useDebouncedCallback(() => {
    if (selectedCategory) {
      searchProducts('', selectedCategory.code)
    } else {
      setProducts([])
    }
  }, 100)

  const debouncedSearch = useDebouncedCallback(
    async (value: string, categoryCode?: string) => {
      await searchProducts(value, categoryCode)
    },
    500,
  )

  const onCategoryClick = (event: MouseEvent<HTMLElement>) => {
    setCategoryMenuAnchor(event.currentTarget)
  }

  const onCategoryMenuClose = () => {
    setCategoryMenuAnchor(null)
  }

  const onCategorySelect = async (category: Category) => {
    setSelectedCategory(category)
    setCategoryMenuAnchor(null)
    await searchProducts(value, category.code)
  }

  useEffect(() => {
    if (selectedCategory) {
      searchProducts('', selectedCategory.code)
    }
  }, [])

  const canShowProducts = sortedProducts.length > 0
  const showNoResults = !isLoading && sortedProducts.length === 0

  const theme = useTheme()

  const renderRow = (props: ListChildComponentProps) => {
    /* eslint-disable react/prop-types */
    const { index, style } = props

    const rowProduct = sortedProducts[index]

    const priceColor =
      rowProduct.unit !== product?.unit
        ? theme.palette.custom.orange2
        : undefined

    return (
      <ProductItem style={style as never}>
        <ProductCard
          product={rowProduct}
          backgroundColor="transparent"
          priceColor={priceColor}
          onClick={onProductClick}
        />
      </ProductItem>
    )
  }

  return (
    <>
      <Wrapper>
        <Header>
          <ReplaceTitle>{t('replaceProductDrawer.title')}</ReplaceTitle>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Header>
        {replaceFromProduct && <ProductCard product={replaceFromProduct} />}
        <SearchProductsWrapper>
          <CategoryRow>
            <CategorySelectorWrapper onClick={onCategoryClick}>
              <CategoryName>
                {selectedCategory?.name ??
                  t('replaceProductDrawer.selectCategory')}
              </CategoryName>
              <ChevronDownIcon />
            </CategorySelectorWrapper>
            <SortIconWrapper>
              <IconButton size="small" color="inherit" onClick={onSort}>
                {reverseProducts ? <ArrowUpIcon /> : <ArrowDownIcon />}
              </IconButton>
            </SortIconWrapper>
          </CategoryRow>
          <SearchRow>
            <SearchInputWrapper>
              <SearchInput
                hideIcon
                value={value}
                placeholder={t('replaceProductDrawer.searchPlaceholder')}
                onChange={onChange}
              />
            </SearchInputWrapper>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
          </SearchRow>
        </SearchProductsWrapper>
        <Menu
          anchorEl={categoryMenuAnchor}
          open={Boolean(categoryMenuAnchor)}
          PaperProps={{
            sx: { maxHeight: 300, width: 280 },
          }}
          onClose={onCategoryMenuClose}
        >
          <MenuItem onClick={() => onCategorySelect(allCategory)}>
            {allCategory.name}
          </MenuItem>
          {categories
            .filter((cat) => cat.code !== 'UNAVAILABLE')
            .map((category) => (
              <MenuItem
                key={category.id}
                onClick={() => onCategorySelect(category)}
              >
                {category.name}
              </MenuItem>
            ))}
        </Menu>
        <ProductList>
          {isLoading ? (
            <LoaderWrapper>
              <CircularProgress />
            </LoaderWrapper>
          ) : canShowProducts ? (
            <AutoSizer>
              {({ width, height }) => (
                <FixedSizeList
                  height={height}
                  width={width}
                  itemSize={88}
                  itemCount={sortedProducts.length}
                  overscanCount={5}
                >
                  {renderRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          ) : showNoResults ? (
            <PlaceholderWrapper>
              <NothingFoundPlaceholder />
            </PlaceholderWrapper>
          ) : null}
        </ProductList>
      </Wrapper>

      <ReplaceProductDialog
        replaceFrom={replaceFromProduct}
        replaceTo={replaceToProduct}
        onClose={onCloseReplaceDialog}
        onComplete={onCompleteReplace}
      />
    </>
  )
}

export default ReplaceProductDrawer
